// src/components/chat/useRefineRuntime.js — assistant-ui runtime adapter
// for the content-editor refine flow.
//
// Unlike the tutor chat, refine has no server-side session — each turn
// is a single POST /content/refine call. We keep a local message log so
// the user can iterate ("shorten the intro" → "now add an example") and
// see prior turns in the panel. The agent's "reply" is a one-line summary;
// the actual rewrite is applied to the editor's textarea via `onApply`.
import { useExternalStoreRuntime } from '@assistant-ui/react';
import { useRef, useState } from 'react';
import { apiService, handleApiError } from '../../services/api';

let nextId = 0;
const id = () => `m${++nextId}`;

export default function useRefineRuntime({
  courseId,
  domain,
  concept,
  contentType,
  currentContent,
  onApply,
}) {
  const [messages, setMessages] = useState([]);  // assistant-ui shape
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState(null);
  // Keep the latest currentContent in a ref so the onNew closure (frozen
  // on first render) can always read the user's most recent edits.
  const contentRef = useRef(currentContent);
  contentRef.current = currentContent;

  const runtime = useExternalStoreRuntime({
    isRunning,
    messages,
    // Going through convertMessage triggers the runtime's normalization
    // pass which fills in default `metadata`. Without it, downstream code
    // crashes on `msg.metadata.unstable_state`.
    convertMessage: (m) => m,
    onNew: async (msg) => {
      const text =
        msg.content?.find((p) => p.type === 'text')?.text ||
        (typeof msg.content === 'string' ? msg.content : '');
      if (!text.trim()) return;

      setIsRunning(true);
      setError(null);

      // Optimistic user message
      const userMsg = {
        id: id(),
        role: 'user',
        content: [{ type: 'text', text }],
        createdAt: new Date(),
      };
      setMessages((m) => [...m, userMsg]);

      try {
        const res = await apiService.refineContent({
          courseId,
          domain,
          concept,
          contentType,
          instruction: text,
          currentContent: contentRef.current || '',
        });
        const { new_content, summary } = res.data;
        if (new_content) onApply(new_content);
        const agentMsg = {
          id: id(),
          role: 'assistant',
          content: [{ type: 'text', text: summary || 'Updated.' }],
          createdAt: new Date(),
        };
        setMessages((m) => [...m, agentMsg]);
      } catch (err) {
        const errText = handleApiError(err);
        setError(errText);
        setMessages((m) => [
          ...m,
          {
            id: id(),
            role: 'assistant',
            content: [{ type: 'text', text: `Couldn't refine: ${errText}` }],
            createdAt: new Date(),
          },
        ]);
      } finally {
        setIsRunning(false);
      }
    },
  });

  return { runtime, error, hasMessages: messages.length > 0 };
}
